<template>
  <div class="flex flex-col relative">
    <label class="rp-fs-13-ln block mb-1 font-bold" :for="props.id">{{ props.label }}</label>
    <div
      :class="props.disabled
        ? 'pointer-events-none bg-transparent focus:outline-none'
        : 'bg-white cursor-pointer'"
      class="rp-fs-15-ln block w-full
        p-3
        border border-ryd-gray3 rounded-md 
        focus:border-ryd-gray5 focus:outline-none focus:ring-1 focus:ring-ryd-gray5"
      tabindex="0"
      @click="selectClick"
      @keydown.enter="selectClick"
    >
      <span v-if="props.defaultValue" class="block">
        {{ props.displayValue
          ? (props.defaultValue as RydSelectOption)?.[props.displayValue as string | number]
          : props.defaultValue
        }}
      </span>
      <span v-else class="block text-ryd-gray4">{{ props.placeholder }}</span>
      <RydIcon icon="arrow-dropdown" class=" absolute top-10 right-2" :class="[props.disabled ? '' : 'text-ryd-primary']" />
    </div>
    <input :id="props.id" type="hidden" :name="props.name" :value="props.defaultValue" />
  </div>

  <RydModalBottom v-if="selectModalOpen" 
                  :customClass="[props.isSearchable ? 'min-h-[90%] h-[90%]' : 'min-h-[430px]']"
                  :a11yName="props.a11yName"
                  @close="selectModalOpen = false"
  >
    <div class="w-full pt-10">
      <p class="rp-fs-16-ln font-semibold text-left">
        {{ props.label }} 
      </p>
      <slot />

      <RydDivider class="my-3" />
    </div>
    <ul class="scrollbar overflow-hidden mt-8"
        :class="[props.isSearchable ? 'max-h-fit h-[70%]' : 'max-h-[260px] h-[260px]']"
    >
      <li v-if="!props.options.length" class="text-center text-ryd-gray4 text-fs-16">
        {{ $t('car.modal.search_no_results') }}
      </li>
      <li v-for="(option, index) in options" :key="index" class="mb-6">
        <!-- In case the option is an object we want to display a specific property -->
        <template v-if="props.displayValue && typeof option === 'object'">
          <RydRadio :id="`${option[props.idValue]}`"
                    v-model="selectedOption"
                    name="selectRadio"
                    :value="option"
                    :label="option[props.displayValue as string]"
                    :checked="isSelected(option)"
          />
        </template>
        <template v-else>
          <RydRadio :id="`${option}`"
                    v-model="selectedOption"
                    name="selectRadio"
                    :value="(option as string)"
                    :label="(option as string)"
                    :checked="isSelected(option)"
          />
        </template>
      </li>
    </ul>
    <div class="fixed  w-full bottom-0 left-0 pb-4 bg-background">
      <RydDivider class="my-3" />
      <div class="flex justify-between items-center">
        <RydButton outline theme="primary" class="mr-2 w-full min-w-28 max-w-40" @click="selectModalOpen = false">
          {{ $t('car.modal.choice.button.action1') }}
        </RydButton>
        <RydButton theme="primary" class="min-w-28 w-full max-w-40" @click="setOption">
          {{ $t('car.modal.choice.button.action2') }}
        </RydButton>
      </div>
    </div>
  </RydModalBottom>
</template>

<script setup lang="ts">
import RydIcon from '../RydIcon/RydIcon.vue';
import RydModalBottom from '@/components/RydModalBottom/RydModalBottom.vue';
import RydDivider from '../RydDivider.vue';
import RydButton from '../RydButton/RydButton.vue';
import RydRadio from '../RydRadio/RydRadio.vue';

import { ref, PropType, watch, nextTick } from 'vue';

const props = defineProps({
  id: {
    type: String,
    default: () => '',
  },
  name: {
    type: String,
    default: () => '',
  },
  label: {
    type: [String, Number],
    default: () => '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  options: {
    type: Array as PropType<RydSelectOption[] | string[] | number[]>,
    default: () => [],
  },
  displayValue: {
    type: [String, Boolean, Number],
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  modalScrollHeight: {
    type: String,
    default: () => 'h-full',
  },
  defaultValue: {
    type: [Object, String, Number] as PropType<RydSelectOption | string | number>,
    default: null,
  },
  a11yName: {
    type: String,
    default: () => '',
  },
  idValue: {
    type: [String, Number],
    default: null,
  },
  isSearchable: {
    type: Boolean,
    default: false,
  }
});

type RydSelectOption = {
  id: string;
  [key: string]: string | number;
};

const emits = defineEmits(['update']);

const selectModalOpen = ref(false);

const isSelected = (option: RydSelectOption | string | number) => {
  if (!props.defaultValue) return;

  if (props.displayValue && typeof option === 'object' && typeof props.defaultValue === 'object') {

    return props.defaultValue[props.displayValue as string] === option[props.displayValue as string];
  }
  return props.defaultValue === option;
};

const selectedOption = ref(props.defaultValue);

const setOption = () => {
  emits('update', selectedOption.value);
  selectModalOpen.value = false;
};

const selectClick = () => {
  if (props.disabled) return;
  selectModalOpen.value = true;
};


// Scroll to element
watch(selectModalOpen, async (newVal) => {
  if (!props.defaultValue) return false;

  if (newVal) {
    // Wait for the DOM to update with the radio buttons
    await nextTick();

    let elementId = '';
    if (typeof props.defaultValue === 'object' && props.defaultValue !== null) {
      elementId = `${props.defaultValue[props.idValue as string]}_selectRadio`;
    } else {
      elementId = `${props.defaultValue}_selectRadio`;
    }
    const element = document.getElementById(elementId);

    if (element) {
      element.scrollIntoView({ block: 'center' });
    }
  }
});
</script>
<style lang="scss" scoped>
  .scrollbar {
    scrollbar-color: #265399 #E6E6E6;
    scrollbar-width: thin;
    overflow-y: scroll;
    scroll-snap-type: y mandatory;

    &label {
      scroll-snap-align: start;
    }


    /* Webkit scrollbar styling for better cross-browser support */
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background-color: #E6E6E6;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #265399;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: #1e40af;
    }
  }

</style>